import { useEffect, useState } from 'react';
import {
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Tooltip,
} from '@mui/material';
import { Plus } from 'lucide-react';
import Swal from 'sweetalert2';
import { getDoorScripts, createDoorScript } from '../../helper/backend_helper';

// Dropdown of saved door scripts + a "+" to add a new one (name + script body).
// `value` is the selected script's name; it is what gets stored on the door and resolved
// by the backend at open time. Paste secrets as <PLACEHOLDER> tokens (e.g. <SHELLY_API_KEY>).
export default function ScriptSelect({ value, onChange }) {
  const [scripts, setScripts] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async (selectName) => {
    try {
      const res = await getDoorScripts();
      const list = res?.data?.data || [];
      setScripts(list);
      if (selectName) {
        onChange(selectName);
      } else if (!value && list.length) {
        onChange(list[0].name); // default to the first script (Shelly 1 API call)
      }
    } catch (error) {
      console.error('Error loading door scripts:', error);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async () => {
    if (!name.trim() || !content.trim()) {
      Swal.fire({ icon: 'warning', title: 'Name and script are required', timer: 1800, showConfirmButton: false });
      return;
    }
    try {
      setSaving(true);
      await createDoorScript({ name: name.trim(), content });
      const savedName = name.trim();
      setSaving(false);
      setOpen(false);
      setName('');
      setContent('');
      await load(savedName);
    } catch (error) {
      setSaving(false);
      Swal.fire({ icon: 'error', title: 'Could not save script', timer: 2000, showConfirmButton: false });
    }
  };

  // Keep a legacy/manual value selectable even if it is not in the saved list.
  const hasValue = value && scripts.some((s) => s.name === value);

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Select fullWidth value={value || ''} onChange={(e) => onChange(e.target.value)} displayEmpty>
          {scripts.length === 0 && !value && (
            <MenuItem value="" disabled>
              No scripts yet — add one with +
            </MenuItem>
          )}
          {value && !hasValue && <MenuItem value={value}>{value}</MenuItem>}
          {scripts.map((s) => (
            <MenuItem key={s._id} value={s.name}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
        <Tooltip title="Add a new script" arrow>
          <IconButton onClick={() => setOpen(true)} aria-label="Add script" sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Plus size={18} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Add script</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Script name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              placeholder="e.g. Shelly 2 API call"
            />
            <TextField
              label="Script (Python)"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              multiline
              minRows={12}
              placeholder="Paste the Python script here. Use <SHELLY_API_KEY> as a placeholder for the key — it is filled in from Shelly Settings at run time."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving}>
            {saving ? 'Saving…' : 'Save script'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
